import type { ReactNode } from 'react';
import { CalendarDays, ChevronRight, FileText, StickyNote, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

import { initialsOf } from '../../../lib/bureau/avatar';
import { dayLabel, euros, type Exchange } from '../../../lib/crm/accountSheet';
import type { CRMAccount, CRMContact, CRMOpportunity } from '../../../types/crm';
import { BLOCK_ROW, REF } from '../../bureau/encre';
import { Pastille, SheetBlock, SheetEmpty, SheetLoading } from './EncreSheet';

/** Where the rows of a block stand: loading, unreadable, or read (`rows`). */
export interface RowsState {
  loading: boolean;
  error: string | null;
}

/** A block's body: a quiet bar while its rows load, the error when they cannot be read, else the rows or the empty line. */
function Rows({ state, empty, emptyText, children }: { state: RowsState; empty: boolean; emptyText: string; children: ReactNode }) {
  if (state.error) return <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {state.error}</p>;
  if (state.loading) return <SheetLoading />;
  return empty ? <SheetEmpty>{emptyText}</SheetEmpty> : <>{children}</>;
}

/** A line that opens something (mock-up « Fiche », « Missions liées »): what, then how it stands. */
function SheetRow({ lead, title, detail, onOpen }: { lead?: ReactNode; title: string; detail: string | null; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className={BLOCK_ROW}>
      {lead}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-desk-ink">{title}</span>
        {detail && <span className="block truncate text-xs text-desk-soft">{detail}</span>}
      </span>
      <ChevronRight size={16} className="shrink-0 text-desk-faint" aria-hidden="true" />
    </button>
  );
}

/** « En cours »: the open deals, each opening its sheet. */
export function OpportunitiesBlock({ opportunities, state, onOpen }: { opportunities: CRMOpportunity[]; state: RowsState; onOpen: (id: string) => void }) {
  return (
    <SheetBlock title="En cours" count={opportunities.length}>
      <Rows state={state} empty={opportunities.length === 0} emptyText="Aucune opportunité en cours.">
        {opportunities.map((o) => (
          <SheetRow
            key={o.id}
            lead={<Pastille icon={Target} />}
            title={o.name}
            detail={[o.stage?.name, euros(o.amount)].filter(Boolean).join(' · ') || null}
            onOpen={() => onOpen(o.id)}
          />
        ))}
      </Rows>
    </SheetBlock>
  );
}

/** A person as a « Contacts » row reads it (an account's contact, a deal's). */
export type SheetContact = Pick<CRMContact, 'id' | 'title' | 'email' | 'phone'> & { first_name: string | null; last_name: string | null };

/** « Contacts »: the people to reach first, each opening its sheet; the rest on the full record. */
export function ContactsBlock({
  contacts,
  total,
  state,
  onOpen,
  more,
}: {
  contacts: SheetContact[];
  total: number;
  state: RowsState;
  onOpen: (id: string) => void;
  /** « Tout voir » when there are more than shown. */
  more: ReactNode;
}) {
  return (
    <SheetBlock title="Contacts" count={total} action={total > contacts.length ? more : undefined}>
      <Rows state={state} empty={contacts.length === 0} emptyText="Aucun contact rattaché.">
        {contacts.map((c) => {
          const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
          return (
            <SheetRow
              key={c.id}
              lead={
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-desk-subtle text-xs font-medium text-desk-ink">
                  {initialsOf(name)}
                </span>
              }
              title={name}
              detail={[c.title, c.email ?? c.phone].filter(Boolean).join(' · ') || null}
              onOpen={() => onOpen(c.id)}
            />
          );
        })}
      </Rows>
    </SheetBlock>
  );
}

/**
 * « Échanges »: the latest appointments, notes and meeting reports in one timeline (mock-up
 * « Fiche »): the day, then what happened and with whom. An appointment or a report opens its
 * sheet, a note its page.
 */
export function ExchangesBlock({
  exchanges,
  state,
  onOpenAppointment,
  onOpenReport,
  noteHref,
  onNavigate,
}: {
  exchanges: Exchange[];
  state: RowsState;
  onOpenAppointment: (id: string) => void;
  /** A meeting report opens its sheet. */
  onOpenReport?: (id: string) => void;
  noteHref: (id: string) => string;
  onNavigate: () => void;
}) {
  return (
    <SheetBlock title="Échanges">
      <Rows state={state} empty={exchanges.length === 0} emptyText="Aucun rendez-vous ni note pour l'instant.">
        <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-4 gap-y-3">
          {exchanges.map((e) => (
            <div key={`${e.kind}-${e.id}`} className="contents">
              <dt className="pt-1 text-sm text-desk-muted">{dayLabel(e.at)}</dt>
              <dd className="m-0 flex min-w-0 items-start gap-2.5 text-sm text-desk-strong">
                <Pastille icon={e.kind === 'appointment' ? CalendarDays : e.kind === 'report' ? FileText : StickyNote} />
                <span className="min-w-0 pt-1">
                  {e.kind === 'appointment' ? (
                    <button type="button" onClick={() => onOpenAppointment(e.id)} className={REF}>
                      {e.label}
                    </button>
                  ) : e.kind === 'report' ? (
                    <button type="button" onClick={() => onOpenReport?.(e.id)} className={REF}>
                      {e.label}
                    </button>
                  ) : (
                    <Link to={noteHref(e.id)} onClick={onNavigate} className={REF} data-sensitive-text="">
                      {e.label}
                    </Link>
                  )}
                  {e.who && <span className="text-desk-soft"> · {e.who}</span>}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </Rows>
    </SheetBlock>
  );
}

/** « Entreprise »: what we know of it, in fields; its group opens the group's sheet. */
export function CompanyBlock({
  account,
  parent,
  onOpenAccount,
}: {
  account: CRMAccount;
  parent: { id: string; name: string } | null;
  onOpenAccount: (id: string) => void;
}) {
  const address = [account.address, [account.postal_code, account.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const fields: [string, ReactNode][] = [
    ['Activité', account.industry],
    ['Flotte', account.fleet_size ? `${account.fleet_size} cars` : null],
    ['Région', account.region],
    ['Téléphone', account.phone],
    ['Adresse', address || null],
    ['SIRET', account.siret],
    [
      'Groupe',
      parent ? (
        <button type="button" onClick={() => onOpenAccount(parent.id)} className={REF}>
          {parent.name}
        </button>
      ) : null,
    ],
  ];
  const known = fields.filter(([, value]) => value);
  return (
    <SheetBlock title="Entreprise">
      {known.length === 0 ? (
        <SheetEmpty>Rien de renseigné : la fiche complète permet de compléter.</SheetEmpty>
      ) : (
        <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
          {known.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-desk-muted">{label}</dt>
              <dd className="m-0 min-w-0 break-words text-desk-strong">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </SheetBlock>
  );
}
